/* eslint-disable no-unused-vars */
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Tabs,
  Tooltip,
  TextInput,
  Checkbox,
  Modal,
  ScrollArea,
  Table,
  Flex,
  Loader,
  Pagination
} from '@mantine/core';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import Card from 'components/Card';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import DataTables from 'components/DataTables';
import React from 'react';
import { pathnames } from 'routes/dacum';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { curriculumMappingService } from 'services/dacum/curriculumMapping.service';
import { CurriculumMappingRecord } from 'types/dacum/curriculumMapping';
import {
  IconTrash,
  IconEdit,
  IconEye,
  IconPlus,
  IconSearch,
  IconFilter,
  IconFilterOff,
  IconX
} from '@tabler/icons-react';
import { coreCompetencyService } from 'services/dacum/coreCompetency.service';
import { courseService } from 'services/lcms/course.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import Dates from 'components/Dates';

interface CoreCompetencyData {
  id: string;
  jobPosition: string;
  jobPositions?: Array<{ id: string; name: string }>;
  competencyName: string;
  category: string;
  level: number;
  categoryDescription?: string;
  levelDescription?: string;
  courses: {
    id: string;
    name: string;
    positions: string[];
  }[];
  schoolName: string;
  businessUnitId: string;
  businessUnitName: string;
  jobFamilyId: string;
  jobFamilyName: string;
  managementLevel: string;
  competencyType: string;
  createdAt: string;
  userIn: string;
  userUp: string;
  dateIn: string;
  dateUp: string;
  year: number;
}

const JobPositionCell: React.FC<{
  positions: Array<{ name?: string } | string>;
  maxDisplay?: number;
}> = ({ positions, maxDisplay = 4 }) => {
  const [showAll, setShowAll] = React.useState(false);

  if (!positions || positions.length === 0) return <Text size='sm'>-</Text>;

  const displayPositions = showAll ? positions : positions.slice(0, maxDisplay);
  const hasMore = positions.length > maxDisplay;

  return (
    <Stack gap={2}>
      {displayPositions.map((pos, index) => (
        <Text key={index} size='sm'>
          {index + 1}. {typeof pos === 'object' && pos?.name ? pos.name : String(pos)}
        </Text>
      ))}
      {hasMore && (
        <Button
          variant='subtle'
          size='xs'
          onClick={(e) => {
            e.stopPropagation();
            setShowAll(!showAll);
          }}
        >
          {showAll ? 'Show Less' : `Show ${positions.length - maxDisplay} More`}
        </Button>
      )}
    </Stack>
  );
};

const DetailCompetencyPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const jobFamilyId = searchParams.get('jobfamilyid') || '';
  const managementLevel = searchParams.get('managementlevel') || '';
  const jobProfileId = searchParams.get('jobprofileid') || '';
  const businessUnitId = searchParams.get('businessunitid') || params.id || '';
  const businessUnitName = searchParams.get('businessunitname') || '';
  const jobFamilyName = searchParams.get('jobfamilyname') || '';
  const competencyType = searchParams.get('competencytype') || '';
  const jobFamilyGroupName = searchParams.get('jobfamilygroupname') || '';
  const msSchoolCompetencyManagementID = searchParams.get('msSchoolCompetencyManagementID') || '';
  const pageNumber = Number(searchParams.get('pageNumber')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || 10;
  const searchQuery = searchParams.get('search') || '';

  const [activeTab, setActiveTab] = React.useState<string>('lpd');
  const [searchInput, setSearchInput] = React.useState(searchQuery);
  const [showOnlyUnassigned, setShowOnlyUnassigned] = React.useState<boolean>(false);

  // Separate pagination for DACUM tab - now using URL params
  const dacumPageNumber = Number(searchParams.get('dacumPageNumber')) || 1;
  const dacumPageSize = Number(searchParams.get('dacumPageSize')) || 10;

  // Bulk selection state
  const [selectedCompetencies, setSelectedCompetencies] = React.useState<Set<string>>(new Set());
  const [bulkModalOpened, { open: openBulkModal, close: closeBulkModal }] = useDisclosure(false);

  // Sync search input with URL parameter
  React.useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const isFilterReady = Boolean(jobFamilyId && managementLevel && businessUnitId);

  // Query for Core Competency and Leader Competency types
  const { data: coreCompetencyResp, isLoading: isLoadingCore } = useQuery({
    queryKey: [
      'coreCompetencies',
      businessUnitId,
      jobFamilyId,
      managementLevel,
      competencyType,
      pageNumber,
      pageSize,
      searchQuery,
      showOnlyUnassigned
    ],
    queryFn: async () => {
      try {
        return await coreCompetencyService.list({
          pageNumber,
          pageSize,
          competencyType: competencyType || 'Core Competency',
          jobFamilyId: jobFamilyId || '',
          managementLevel: managementLevel || '',
          businessUnitId: businessUnitId || '',
          search: searchQuery,
          onlyUnassigned: showOnlyUnassigned ? 1 : undefined
        });
      } catch (error: any) {
        // Handle 404 or other errors by returning empty data structure
        if (error?.response?.status === 404 || error?.status === 404) {
          return { data: [], totalRecords: 0, totalPages: 0 };
        }
        throw error;
      }
    },
    enabled:
      (competencyType === 'Core Competency' ||
        competencyType === 'Leader Competency' ||
        competencyType === 'FM Competency') &&
      isFilterReady,
    retry: false
  });

  const coreCompetencies = React.useMemo(() => {
    if (!coreCompetencyResp?.data) return [];
    return coreCompetencyResp.data.map((item: any) => ({
      id: item.msCompetencyManagementID,
      businessUnitId: item.businessUnitId,
      businessUnitName: item.businessUnitName,
      jobFamilyId: item.jobFamilyId,
      jobFamilyName: item.jobFamilyName,
      managementLevel: item.managementLevel,
      competencyType: item.competencyType,
      schoolName: item.schoolName,
      jobPosition: item.jobPosition,
      jobPositions: item.jobPositions, // Include array format if available
      competencyName: item.competencyName,
      competencyDescription: item.competencyDescription,
      category: item.category,
      level: item.level,
      categoryDescription: item.categoryDescription,
      levelDescription: item.levelDescription,
      courses: (item.courses || []).map((course: any) => ({
        id: course.courseId,
        name: course.courseName,
        positions: course.positions || []
      })),
      createdAt: '',
      userIn: item.userIn,
      dateIn: item.dateIn,
      userUp: item.userUp,
      dateUp: item.dateUp,
      year: item.year
    })) as CoreCompetencyData[];
  }, [coreCompetencyResp]);

  // Query for DACUM Competency tab (Technical Competency type)
  const {
    data: dacumCompetenciesResp,
    isLoading: isLoadingDacum,
    refetch: refetchDacum
  } = useQuery({
    queryKey: [
      'dacumCompetencies',
      businessUnitId,
      businessUnitName,
      jobFamilyId,
      jobFamilyName,
      managementLevel,
      competencyType,
      dacumPageNumber,
      dacumPageSize,
      searchQuery,
      showOnlyUnassigned
    ],
    queryFn: async () => {
      try {
        return await coreCompetencyService.list({
          pageNumber: dacumPageNumber,
          pageSize: dacumPageSize,
          competencyType: 'Technical Competency',
          // For Technical Competency, use names instead of IDs
          jobFamilyId: jobFamilyName || '',
          managementLevel: managementLevel || '',
          businessUnitId: businessUnitName || '',
          search: searchQuery,
          onlyUnassigned: showOnlyUnassigned ? 1 : undefined
        });
      } catch (error: any) {
        // Handle 404 or other errors by returning empty data structure
        if (error?.response?.status === 404 || error?.status === 404) {
          return { data: [], totalRecords: 0, totalPages: 0 };
        }
        throw error;
      }
    },
    placeholderData: (previousData) => previousData,
    enabled: competencyType === 'Technical Competency' && isFilterReady && activeTab === 'dacum',
    retry: false,
    staleTime: 0
  });

  const dacumCompetencies = React.useMemo(() => {
    if (!dacumCompetenciesResp?.data) return [];
    return dacumCompetenciesResp.data.map((item: any) => ({
      id: item.msCompetencyManagementID,
      businessUnitId: item.businessUnitId,
      businessUnitName: item.businessUnitName,
      jobFamilyId: item.jobFamilyId,
      jobFamilyName: item.jobFamilyName,
      managementLevel: item.managementLevel,
      competencyType: item.competencyType,
      schoolName: item.schoolName,
      jobPosition: item.jobPosition,
      jobPositions: item.jobPositions,
      competencyName: item.competencyName,
      competencyDescription: item.competencyDescription,
      category: item.category,
      level: item.level,
      categoryDescription: item.categoryDescription,
      levelDescription: item.levelDescription,
      courses: (item.courses || []).map((course: any) => ({
        id: course.courseId,
        name: course.courseName,
        positions: course.positions || []
      })),
      createdAt: '',
      userIn: item.userIn,
      dateIn: item.dateIn,
      userUp: item.userUp,
      dateUp: item.dateUp,
      year: item.year
    })) as CoreCompetencyData[];
  }, [dacumCompetenciesResp]);

  const deleteMutation = useMutation({
    mutationFn: coreCompetencyService.delete,
    onSuccess: async () => {
      showSuccessNotification({ message: 'Competency deleted successfully' });

      // Invalidate and refetch queries to force update
      if (
        competencyType === 'Core Competency' ||
        competencyType === 'Leader Competency' ||
        competencyType === 'FM Competency'
      ) {
        await queryClient.invalidateQueries({ queryKey: ['coreCompetencies'] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['dacumCompetencies'] });
      }

      // Also invalidate curriculum mapping if on Technical Competency
      if (competencyType === 'Technical Competency') {
        await queryClient.invalidateQueries({ queryKey: ['curriculumMapping'] });
      }
    },
    onError: (error: any) => {
      showErrorNotification({ message: error?.message || 'Failed to delete competency' });
    }
  });

  const handleDeleteDacumCompetency = (id: string) => {
    if (!window.confirm('Are you sure you want to delete this competency?')) {
      return;
    }
    deleteMutation.mutate(id);
  };

  const handleEditCompetency = (competency: CoreCompetencyData) => {
    const query = new URLSearchParams(searchParams);

    if (uniqueJobPositions.length > 0) {
      query.set('lpdJobPositions', JSON.stringify(uniqueJobPositions));
    }

    navigate(
      {
        pathname: pathnames.editCoreCompetency.replace(':id', competency.id),
        search: query.toString()
      },
      {
        replace: false,
        state: { competencyDescription: (competency as any).competencyDescription || '' }
      }
    );
  };

  const handleAddCoreCompetency = () => {
    const query = new URLSearchParams(searchParams);

    if (uniqueJobPositions.length > 0) {
      query.set('lpdJobPositions', JSON.stringify(uniqueJobPositions));
    }

    navigate(
      {
        pathname: pathnames.addCoreCompetency,
        search: query.toString()
      },
      { replace: false }
    );
  };

  const headerDescription =
    'Review competency mapping for the selected job family, job band, and business unit.';
  const emptyStateMessage =
    'Select a job family, job band, and business unit from the previous page to view competency details.';

  const updateParams = React.useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      let hasChanges = false;

      Object.entries(updates).forEach(([key, value]) => {
        const currentValue = searchParams.get(key);

        if (value === null) {
          if (searchParams.has(key)) {
            next.delete(key);
            hasChanges = true;
          }
          return;
        }

        if (currentValue !== value) {
          next.set(key, value);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  const getJobProfileId = React.useCallback((row: CurriculumMappingRecord) => {
    const fallback = (row as any).jobProfileID || (row as any).jobprofileid;
    return (
      row.jobProfileId || (row as any).job?.[0]?.jobPositionId || fallback || row.jobPositionId
    );
  }, []);

  const handleSelectJobProfile = React.useCallback(
    (row: CurriculumMappingRecord) => {
      const profileId = getJobProfileId(row);
      if (!profileId || profileId === jobProfileId) return;
      updateParams({ jobprofileid: profileId, pageNumber: '1' });
    },
    [getJobProfileId, jobProfileId, updateParams]
  );

  const clearJobProfileFilter = React.useCallback(() => {
    if (!jobProfileId) return;
    updateParams({ jobprofileid: null, pageNumber: '1' });
  }, [jobProfileId, updateParams]);

  const handleChangePage = React.useCallback(
    (page: number) => {
      if (page === pageNumber) return;
      updateParams({ pageNumber: page.toString() });
    },
    [pageNumber, updateParams]
  );

  const handleChangeSize = React.useCallback(
    (size: number) => {
      if (size === pageSize) return;
      updateParams({ pageSize: size.toString(), pageNumber: '1' });
    },
    [pageSize, updateParams]
  );

  // DACUM tab pagination handlers
  const handleDacumChangePage = React.useCallback(
    (page: number) => {
      if (page === dacumPageNumber) return;
      updateParams({ dacumPageNumber: page.toString() });
    },
    [dacumPageNumber, updateParams]
  );

  const handleDacumChangeSize = React.useCallback(
    (size: number) => {
      if (size === dacumPageSize) return;
      updateParams({ dacumPageSize: size.toString(), dacumPageNumber: '1' });
    },
    [dacumPageSize, updateParams]
  );

  const handleSearch = React.useCallback(
    (value: string) => {
      const nextValue = value ? value : null;
      if (nextValue === null && !searchQuery) return;
      if (nextValue !== null && nextValue === searchQuery) return;
      updateParams({ search: nextValue, pageNumber: '1', dacumPageNumber: '1' });
    },
    [searchQuery, updateParams]
  );

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    handleSearch('');
  };

  const {
    data: curriculumMappingResp,
    isLoading,
    refetch: refetchCurriculumMapping
  } = useQuery({
    queryKey: [
      'curriculumMapping',
      jobFamilyId,
      managementLevel,
      businessUnitId,
      jobProfileId,
      pageNumber,
      pageSize,
      searchQuery,
      showOnlyUnassigned
    ],
    queryFn: () =>
      curriculumMappingService.getCurriculumMapping({
        jobfamilyid: jobFamilyId,
        managementlevel: managementLevel,
        businessunitid: businessUnitId,
        jobprofileid: jobProfileId || undefined,
        pageNumber,
        pageSize,
        search: searchQuery,
        onlyUnassigned: showOnlyUnassigned ? true : false
      }),
    enabled: isFilterReady
  });

  const curriculumData = React.useMemo(
    () => curriculumMappingResp?.data || [],
    [curriculumMappingResp?.data]
  );

  const [curriculumSelection, setCurriculumSelection] = React.useState<CurriculumMappingRecord[]>(
    []
  );

  React.useEffect(() => {
    setCurriculumSelection(curriculumData);
  }, [curriculumData]);

  const buildCurriculumKey = React.useCallback(
    (record: CurriculumMappingRecord) =>
      `${(record as any).job?.[0]?.jobPositionId || record.jobPositionId}-${record.competencyId}-${record.category}-${record.level}`,
    []
  );

  const handleRemoveCurriculum = React.useCallback(
    (record: CurriculumMappingRecord) => {
      if (!jobProfileId) return;
      if (
        !window.confirm(
          'Remove this curriculum entry from the current selection? You can restore it before creating the master plan.'
        )
      ) {
        return;
      }
      setCurriculumSelection((prev) =>
        prev.filter((item) => buildCurriculumKey(item) !== buildCurriculumKey(record))
      );
    },
    [buildCurriculumKey, jobProfileId]
  );

  const handleRestoreCurriculum = React.useCallback(() => {
    setCurriculumSelection(curriculumData);
  }, [curriculumData]);

  // Columns for DACUM Competency tab (second tab)
  const dacumColumns = React.useMemo(() => {
    return [
      {
        heading: 'Competency Type',
        props: 'competencyType',
        sortable: true,
        active: true,
        id: 'competencyType'
      },
      {
        heading: 'Job Position',
        id: 'jobPositions',
        sortable: true,
        active: true,
        props: (row: CoreCompetencyData) => {
          if (row.jobPositions && Array.isArray(row.jobPositions) && row.jobPositions.length > 0) {
            return <JobPositionCell positions={row.jobPositions} />;
          }
          return row.jobPosition || '-';
        }
      },
      {
        heading: 'Competency Name',
        props: 'competencyName',
        sortable: true,
        active: true,
        id: 'competencyName'
      },
      {
        heading: 'Competency Description',
        props: 'competencyDescription',
        sortable: true,
        active: true,
        id: 'competencyDescription'
      },
      {
        heading: 'Category',
        props: 'category',
        sortable: true,
        active: true,
        id: 'category'
      },
      {
        heading: 'Category Description',
        props: (row: CoreCompetencyData) => row.categoryDescription || '-',
        sortable: true,
        active: true,
        id: 'categoryDescription'
      },
      {
        heading: 'Level',
        props: 'level',
        sortable: true,
        active: true,
        id: 'level'
      },
      {
        heading: 'Level Description',
        props: (row: CoreCompetencyData) => row.levelDescription || '-',
        sortable: true,
        active: true,
        id: 'levelDescription'
      },
      {
        heading: 'Year',
        props: (row: CoreCompetencyData) => row.year || '-',
        sortable: true,
        active: true,
        id: 'year'
      },
      {
        heading: 'Courses',
        props: (row: CoreCompetencyData) =>
          (row.courses || []).length === 0 ? (
            '-'
          ) : (
            <Stack gap={2}>
              {row.courses.map((c, index) => (
                <Text key={c.id} fz={14}>
                  {index + 1}.{' '}
                  <Anchor
                    href={`/app/lcms/courses/${c.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    size='sm'
                  >
                    {c.name}
                  </Anchor>
                  {/* {c.positions.length > 0 ? ` (${c.positions.join(', ')})` : ''} */}
                </Text>
              ))}
            </Stack>
          ),
        sortable: false,
        active: true,
        id: 'positions'
      },
      {
        heading: 'Created By',
        props: (row: CoreCompetencyData) => row.userIn || '-',
        sortable: true,
        active: true,
        id: 'userIn'
      },
      {
        heading: 'Created At',
        props: (row: CoreCompetencyData) => (
          <div>
            <Text size='sm'>
              <Dates date={row.dateIn} />
            </Text>
          </div>
        ),
        sortable: true,
        active: true,
        id: 'dateIn'
      },
      {
        heading: 'Updated By',
        props: (row: CoreCompetencyData) => row.userUp || '-',
        sortable: true,
        active: true,
        id: 'userUp'
      },
      {
        heading: 'Updated At',
        props: (row: CoreCompetencyData) => (
          <div>
            <Text size='sm'>
              <Dates date={row.dateUp} />
            </Text>
          </div>
        ),
        sortable: true,
        active: true,
        id: 'dateUp'
      },
      {
        heading: 'Actions',
        props: (row: CoreCompetencyData) => (
          <Group justify='flex-end'>
            <Tooltip label='Edit Competency'>
              <ActionIcon
                variant='transparent'
                c='blue.6'
                onClick={() => handleEditCompetency(row)}
              >
                <IconEdit />
              </ActionIcon>
            </Tooltip>
            <Tooltip label='Delete Competency'>
              <ActionIcon
                variant='transparent'
                c='red.6'
                onClick={() => handleDeleteDacumCompetency(row.id)}
              >
                <IconTrash />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
        sortable: false,
        active: true,
        id: 'actions'
      }
    ];
  }, [handleEditCompetency, handleDeleteDacumCompetency]);

  const displayData =
    competencyType === 'Core Competency' ||
    competencyType === 'Leader Competency' ||
    competencyType === 'FM Competency'
      ? coreCompetencies
      : activeTab === 'lpd'
        ? curriculumSelection
        : dacumCompetencies;

  const selectionChanged =
    competencyType !== 'Core Competency' &&
    competencyType !== 'Leader Competency' &&
    competencyType !== 'FM Competency' &&
    Boolean(jobProfileId) &&
    curriculumSelection.length !== curriculumData.length;

  // Bulk action handlers - must be defined before columns useMemo
  const handleToggleSelection = React.useCallback((competencyId: string) => {
    setSelectedCompetencies((prev) => {
      const next = new Set(prev);
      if (next.has(competencyId)) {
        next.delete(competencyId);
      } else {
        next.add(competencyId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = React.useCallback(() => {
    const allIds = curriculumSelection.map((item) => item.competencyId);

    if (selectedCompetencies.size > 0) {
      setSelectedCompetencies(new Set());
    } else {
      setSelectedCompetencies(new Set(allIds));
    }
  }, [selectedCompetencies, curriculumSelection]);

  const handleBulkAction = React.useCallback(() => {
    if (selectedCompetencies.size === 0) {
      showErrorNotification({ message: 'Please select at least one competency' });
      return;
    }
    openBulkModal();
  }, [selectedCompetencies.size, openBulkModal]);

  const handleAddFromLpd = React.useCallback(
    (row: CurriculumMappingRecord) => {
      const query = new URLSearchParams(searchParams);

      // Pass existing filter data
      query.set('businessUnitId', businessUnitId || '');
      query.set('businessUnitName', businessUnitName || '');
      query.set('jobFamilyId', jobFamilyId || '');
      query.set('jobFamilyName', jobFamilyName || '');
      query.set('managementLevel', managementLevel || '');
      query.set('competencyType', competencyType || '');

      // Only pass the competencyId
      if (row.competencyId) {
        query.set('competencyId', row.competencyId);
      }

      // Pass category description directly from LPD row
      if (row.description) {
        query.set('categoryDescription', row.description);
      }

      // Pass level category description from LPD row
      if (row.categoryDescription) {
        query.set('levelDescription', row.categoryDescription);
      }

      // Pass competency description from LPD row
      if ((row as any).competencyDescription) {
        query.set('competencyDescription', (row as any).competencyDescription);
      }

      navigate({
        pathname: pathnames.addCoreCompetency,
        search: query.toString()
      });
    },
    [
      navigate,
      searchParams,
      businessUnitId,
      businessUnitName,
      jobFamilyId,
      jobFamilyName,
      managementLevel,
      competencyType
    ]
  );

  const bulkCreateMutation = useMutation({
    mutationFn: async (data: {
      competencies: CurriculumMappingRecord[];
      courses: any[];
      onProgress?: (index: number, total: number) => void;
    }) => {
      const results = [];
      const total = data.competencies.length;

      // Process each row individually WITHOUT grouping - loop through ALL competencies
      for (let i = 0; i < data.competencies.length; i++) {
        const competency = data.competencies[i];

        // Notify progress
        if (data.onProgress) {
          data.onProgress(i, total);
        }

        // Extract job position for this specific row
        const jobPositionsList: Array<{ id: string; name: string }> = [];

        if (competency.jobPositionId && competency.jobPositionName) {
          jobPositionsList.push({
            id: competency.jobPositionId,
            name: competency.jobPositionName
          });
        }

        const payload: any = {
          businessUnitId:
            competencyType === 'Technical Competency' ? businessUnitName : businessUnitId,
          businessUnitName,
          jobFamilyId: competencyType === 'Technical Competency' ? jobFamilyName : jobFamilyId,
          jobFamilyName,
          managementLevel,
          competencyType,
          schoolName: '',
          jobPositions: jobPositionsList,
          competencyName: competency.competencyName,
          category: competency.category,
          level: parseInt(competency.level),
          categoryDescription: competency.description || '',
          levelDescription: competency.categoryDescription || '',
          note: competency.note || '',
          year: competency.year || new Date().getFullYear(),
          courses: data.courses.map((course) => ({
            courseId: course.courseId,
            courseName: course.courseName,
            positions: jobPositionsList.map((jp) => jp.name)
          }))
        };

        if (competencyType === 'Technical Competency' && competency.competencyId) {
          payload.idLPD = competency.competencyId;
        }

        try {
          const result = await coreCompetencyService.create(payload);
          results.push({ success: true, data: result, row: i });
        } catch (error: any) {
          results.push({ success: false, error: error?.message, row: i });
        }
      }

      return results;
    },
    onSuccess: (results) => {
      const successCount = results.filter((r: any) => r.success).length;
      const failCount = results.filter((r: any) => !r.success).length;

      if (failCount === 0) {
        showSuccessNotification({
          message: `Successfully created ${successCount} competenc${successCount > 1 ? 'ies' : 'y'}`
        });
      } else {
        showSuccessNotification({
          message: `Created ${successCount} competencies. ${failCount} failed.`
        });
      }

      closeBulkModal();
      setSelectedCompetencies(new Set());
      refetchDacum();
      refetchCurriculumMapping();
    },
    onError: (error: any) => {
      showErrorNotification({ message: error?.message || 'Failed to create competencies' });
    }
  });

  const handleBulkCourseSubmit = React.useCallback(
    (data: {
      competencies: any[];
      courses: any[];
      onProgress?: (index: number, total: number) => void;
    }) => {
      bulkCreateMutation.mutate({
        competencies: data.competencies,
        courses: data.courses,
        onProgress: data.onProgress
      });
    },
    [bulkCreateMutation]
  );

  const columns = React.useMemo(() => {
    if (
      competencyType === 'Core Competency' ||
      competencyType === 'Leader Competency' ||
      competencyType === 'FM Competency'
    ) {
      return [
        {
          heading: 'Business Unit',
          props: 'businessUnitName',
          sortable: true,
          active: true,
          id: 'businessUnitName'
        },
        {
          heading: 'Job Family',
          props: 'jobFamilyName',
          sortable: true,
          active: true,
          id: 'jobFamilyName'
        },
        {
          heading: 'Job Band',
          props: 'managementLevel',
          sortable: true,
          active: true,
          id: 'managementLevel'
        },
        {
          heading: 'Competency Type',
          props: 'competencyType',
          sortable: true,
          active: true,
          id: 'competencyType'
        },
        {
          heading: 'School Name',
          props: 'schoolName',
          sortable: true,
          active: true,
          id: 'schoolName'
        },
        {
          heading: 'Job Position',
          id: 'jobPositions',
          sortable: true,
          active: true,
          props: (row: CoreCompetencyData) => {
            // Check if jobPositions array exists and has items
            if (
              row.jobPositions &&
              Array.isArray(row.jobPositions) &&
              row.jobPositions.length > 0
            ) {
              return <JobPositionCell positions={row.jobPositions} />;
            }
            // Fallback to jobPosition string
            return row.jobPosition || '-';
          }
        },
        {
          heading: 'Competency Name',
          props: 'competencyName',
          sortable: true,
          active: true,
          id: 'competencyName'
        },
        {
          heading: 'Competency Description',
          props: 'competencyDescription',
          sortable: true,
          active: true,
          id: 'competencyDescription'
        },
        {
          heading: 'Category',
          props: 'category',
          sortable: true,
          active: true,
          id: 'category'
        },
        {
          heading: 'Category Description',
          props: (row: CoreCompetencyData) => row.categoryDescription || '-',
          sortable: true,
          active: true,
          id: 'categoryDescription'
        },
        {
          heading: 'Level',
          props: 'level',
          sortable: true,
          active: true,
          id: 'level'
        },
        {
          heading: 'Level Description',
          props: (row: CoreCompetencyData) => row.levelDescription || '-',
          sortable: true,
          active: true,
          id: 'levelDescription'
        },
        {
          heading: 'Year',
          props: (row: CoreCompetencyData) => row.year || '-',
          sortable: true,
          active: true,
          id: 'year'
        },
        {
          heading: 'Courses',
          props: (row: CoreCompetencyData) =>
            (row.courses || []).length === 0 ? (
              '-'
            ) : (
              <Stack gap={2}>
                {row.courses.map((c, index) => (
                  <Text key={c.id} fz={14}>
                    {index + 1}.{' '}
                    <Anchor
                      href={`/app/lcms/courses/${c.id}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      size='sm'
                    >
                      {c.name}
                    </Anchor>
                    {c.positions.length > 0 ? ` (${c.positions.join(', ')})` : ''}
                  </Text>
                ))}
              </Stack>
            ),
          sortable: false,
          active: true,
          id: 'courses'
        },
        {
          heading: 'Created By',
          props: (row: CoreCompetencyData) => row.userIn || '-',
          sortable: true,
          active: true,
          id: 'userIn'
        },
        {
          heading: 'Created At',
          props: (row: CoreCompetencyData) => (
            <div>
              <Text size='sm'>
                <Dates date={row.dateIn} />
              </Text>
            </div>
          ),

          sortable: true,
          active: true,
          id: 'dateIn'
        },
        {
          heading: 'Updated By',
          props: (row: CoreCompetencyData) => row.userUp || '-',
          sortable: true,
          active: true,
          id: 'userUp'
        },
        {
          heading: 'Updated At',
          props: (row: CoreCompetencyData) => (
            <div>
              <Text size='sm'>
                <Dates date={row.dateUp} />
              </Text>
            </div>
          ),
          sortable: true,
          active: true,
          id: 'dateUp'
        },
        {
          heading: 'Actions',
          props: (row: CoreCompetencyData) => (
            <Group justify='flex-end'>
              <Tooltip label='Edit Competency'>
                <ActionIcon
                  variant='transparent'
                  c='blue.6'
                  onClick={() => handleEditCompetency(row)}
                >
                  <IconEdit />
                </ActionIcon>
              </Tooltip>
              <Tooltip label='Delete Competency'>
                <ActionIcon
                  variant='transparent'
                  c='red.6'
                  onClick={() => handleDeleteDacumCompetency(row.id)}
                >
                  <IconTrash />
                </ActionIcon>
              </Tooltip>
            </Group>
          ),
          sortable: false,
          active: true,
          id: 'actions'
        }
      ];
    }
    return [
      {
        heading: (
          <Checkbox
            checked={
              selectedCompetencies.size > 0 &&
              selectedCompetencies.size === curriculumSelection.length &&
              curriculumSelection.every((item) => selectedCompetencies.has(item.competencyId))
            }
            indeterminate={
              selectedCompetencies.size > 0 &&
              selectedCompetencies.size < curriculumSelection.length
            }
            onChange={handleToggleAll}
          />
        ),
        props: (row: CurriculumMappingRecord) => (
          <Checkbox
            checked={selectedCompetencies.has(row.competencyId)}
            onChange={(e) => {
              e.stopPropagation();
              handleToggleSelection(row.competencyId);
            }}
          />
        ),
        sortable: false,
        active: competencyType === 'Technical Competency',
        id: 'select'
      },
      {
        heading: 'Job Position',
        props: (row: CurriculumMappingRecord) => {
          if ((row as any).job && Array.isArray((row as any).job) && (row as any).job.length > 0) {
            const jobs = (row as any).job.map((j: any) => ({ name: j.jobPositionName }));
            return <JobPositionCell positions={jobs} />;
          }
          return row.jobPositionName || '-';
        },
        sortable: true,
        active: true,
        id: 'jobPositionName'
      },
      {
        heading: 'Competency Name',
        props: 'competencyName',
        sortable: true,
        active: true,
        id: 'competencyName'
      },
      {
        heading: 'Competency Description',
        props: 'competencyDescription',
        sortable: true,
        active: true,
        id: 'competencyDescription'
      },
      {
        heading: 'Level',
        props: 'level',
        sortable: true,
        active: true,
        id: 'level'
      },
      {
        heading: 'Category',
        props: 'category',
        sortable: true,
        active: true,
        id: 'category'
      },
      {
        heading: 'Category Description',
        props: 'description',
        sortable: true,
        active: true,
        id: 'description'
      },

      // {
      //   heading: 'Level Description',
      //   props: (row: CurriculumMappingRecord) => row.levelDescription || '-',
      //   sortable: true,
      //   active: true,
      //   id: 'levelDescription'
      // },

      {
        heading: 'Course From LPD',
        props: (row: CurriculumMappingRecord) => row.courseName || '-',
        sortable: false,
        active: true,
        id: 'courseName'
      },
      {
        heading: 'Course DACUM Competency',
        props: (row: any) => {
          const dacumCurriculum = row.dacumCurriculum;
          if (!dacumCurriculum || !Array.isArray(dacumCurriculum) || dacumCurriculum.length === 0) {
            return '-';
          }

          // Collect all courses from all dacum items
          const allCourses: Array<{ courseId: string; courseName: string }> = [];
          dacumCurriculum.forEach((dacum: any) => {
            if (dacum.courses && Array.isArray(dacum.courses)) {
              dacum.courses.forEach((course: any) => {
                if (course.courseId && course.courseName) {
                  allCourses.push({
                    courseId: course.courseId,
                    courseName: course.courseName
                  });
                }
              });
            }
          });

          if (allCourses.length === 0) {
            return '-';
          }

          return (
            <Stack gap={4}>
              {allCourses.map((course, index) => (
                <Text key={course.courseId} fz={14}>
                  {index + 1}. {course.courseName}
                </Text>
              ))}
            </Stack>
          );
        },
        sortable: false,
        active: true,
        id: 'dacumCompetency'
      },
      {
        heading: 'Action',
        props: (row: CurriculumMappingRecord) => {
          const profileId = getJobProfileId(row);
          const isActive = profileId && jobProfileId === profileId;

          return (
            <Group justify='flex-start'>
              {!isActive && (
                <Tooltip label='Filter by Job Profile'>
                  <ActionIcon
                    variant='light'
                    color='blue'
                    onClick={() => handleSelectJobProfile(row)}
                    disabled={!profileId}
                  >
                    <IconEye />
                  </ActionIcon>
                </Tooltip>
              )}

              {competencyType === 'Technical Competency' && (
                <Tooltip label='Add to DACUM Competency'>
                  <ActionIcon variant='light' color='green' onClick={() => handleAddFromLpd(row)}>
                    <IconPlus />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          );
        },
        sortable: false,
        active: true,
        id: 'actions'
      }
    ];
  }, [
    competencyType,
    getJobProfileId,
    handleRemoveCurriculum,
    handleSelectJobProfile,
    handleAddFromLpd,
    handleToggleSelection,
    handleToggleAll,
    selectedCompetencies,
    curriculumSelection,
    jobProfileId
  ]);

  // Extract and group unique job positions from curriculum data
  const uniqueJobPositions = React.useMemo(() => {
    const positionsMap = new Map<string, { id: string; name: string }>();

    curriculumData.forEach((item) => {
      const jobs = (item as any).job;
      if (jobs && Array.isArray(jobs)) {
        jobs.forEach((j: any) => {
          if (j.jobPositionId && j.jobPositionName) {
            positionsMap.set(j.jobPositionId, {
              id: j.jobPositionId,
              name: j.jobPositionName
            });
          }
        });
      } else if (item.jobPositionId && item.jobPositionName) {
        positionsMap.set(item.jobPositionId, {
          id: item.jobPositionId,
          name: item.jobPositionName
        });
      }
    });

    return Array.from(positionsMap.values());
  }, [curriculumData]);

  const breadcrumbs = [
    { title: 'Competency Management', href: pathnames.competencyManagement },
    { title: 'Detail Competency', href: '' }
  ];

  return (
    <Container fluid>
      <BreadcrumbsComponent items={breadcrumbs} mb={32} />

      <Stack gap={24}>
        <Paper p='xl' radius='md' shadow='sm' withBorder>
          <Group justify='space-between' align='flex-start' mb='lg'>
            <Stack gap='xs'>
              <Title order={2} fw={600}>
                Competency Detail
              </Title>
              <Text size='sm' c='dimmed'>
                {headerDescription}
              </Text>
            </Stack>
            <Group>
              {(competencyType === 'Core Competency' ||
                competencyType === 'Leader Competency' ||
                competencyType === 'FM Competency') && (
                <Button
                  onClick={handleAddCoreCompetency}
                  leftSection={<IconPlus size={18} />}
                  variant='filled'
                  size='md'
                >
                  Add{' '}
                  {competencyType === 'Leader Competency'
                    ? 'Leader'
                    : competencyType === 'FM Competency'
                      ? 'FM'
                      : 'Core'}{' '}
                  Competency
                </Button>
              )}
              {(competencyType === 'Core Competency' ||
                competencyType === 'Leader Competency' ||
                competencyType === 'FM Competency') &&
                coreCompetencies.length > 0 && (
                  <Button
                    onClick={() => {
                      const query = new URLSearchParams(searchParams);

                      let groupName = jobFamilyGroupName;
                      if (!groupName) {
                        groupName = 'Technical';
                      }
                      if (groupName) {
                        query.set('jobfamilygroupname', groupName);
                      }

                      if (msSchoolCompetencyManagementID) {
                        query.set('msSchoolCompetencyManagementID', msSchoolCompetencyManagementID);
                      }

                      const curriculumData = (coreCompetencyResp?.data || []).map((item) => ({
                        competencyId: item.msCompetencyManagementID,
                        businessUnitId: item.businessUnitId,
                        businessUnitName: item.businessUnitName,
                        jobPositionId: item.jobPosition || '',
                        jobPositionName: item.jobPosition || '-',
                        jobPositions: item.jobPositions,
                        jobProfileId: undefined,
                        jobBand: item.managementLevel,
                        competencyName: item.competencyName,
                        competencyDescription: item.categoryDescription || '',
                        category: item.category,
                        level: item.level.toString(),
                        levelDescription: item.levelDescription || '',
                        description: '',
                        idCourse:
                          item.courses && item.courses.length > 0 ? item.courses[0].courseId : null,
                        courseName:
                          item.courses && item.courses.length > 0
                            ? item.courses[0].courseName
                            : null,
                        courses: (item.courses || []).map((c: any) => ({
                          courseId: c.courseId,
                          courseName: c.courseName
                        })),
                        year: item.year
                      }));

                      navigate(
                        {
                          pathname: pathnames.createMasterPlan,
                          search: query.toString()
                        },
                        {
                          state: { curriculumData, msSchoolCompetencyManagementID }
                        }
                      );
                    }}
                    variant='filled'
                    size='md'
                    color='green'
                  >
                    Submit Masterplan
                  </Button>
                )}
              {competencyType === 'Technical Competency' &&
                activeTab === 'dacum' &&
                dacumCompetencies.length > 0 && (
                  <Button
                    onClick={() => {
                      const query = new URLSearchParams(searchParams);

                      let groupName = jobFamilyGroupName;
                      if (!groupName) {
                        groupName = 'Technical';
                      }
                      if (groupName) {
                        query.set('jobfamilygroupname', groupName);
                      }

                      if (msSchoolCompetencyManagementID) {
                        query.set('msSchoolCompetencyManagementID', msSchoolCompetencyManagementID);
                      }

                      const curriculumData = (dacumCompetenciesResp?.data || []).map((item) => ({
                        competencyId: item.msCompetencyManagementID,
                        businessUnitId: item.businessUnitId,
                        businessUnitName: item.businessUnitName,
                        jobPositionId: item.jobPosition || '',
                        jobPositionName: item.jobPosition || '-',
                        jobPositions: item.jobPositions,
                        jobProfileId: undefined,
                        jobBand: item.managementLevel,
                        competencyName: item.competencyName,
                        competencyDescription: item.categoryDescription || '',
                        category: item.category,
                        level: item.level.toString(),
                        levelDescription: item.levelDescription || '',
                        description: '',
                        idCourse:
                          item.courses && item.courses.length > 0 ? item.courses[0].courseId : null,
                        courseName:
                          item.courses && item.courses.length > 0
                            ? item.courses[0].courseName
                            : null,
                        courses: (item.courses || []).map((c: any) => ({
                          courseId: c.courseId,
                          courseName: c.courseName
                        })),
                        year: item.year
                      }));

                      navigate(
                        {
                          pathname: pathnames.createMasterPlan,
                          search: query.toString()
                        },
                        {
                          state: { curriculumData, msSchoolCompetencyManagementID }
                        }
                      );
                    }}
                    variant='filled'
                    size='md'
                    color='green'
                  >
                    Submit Masterplan
                  </Button>
                )}
            </Group>
          </Group>

          <Stack gap='md'>
            <Group gap='sm'>
              <Badge size='lg' color='blue' variant='light' radius='md'>
                Business Unit: {businessUnitName || businessUnitId || '-'}
              </Badge>
              <Badge size='lg' color='green' variant='light' radius='md'>
                Job Family: {jobFamilyName || jobFamilyId || '-'}
              </Badge>
              <Badge size='lg' color='grape' variant='light' radius='md'>
                Job Band: {managementLevel || '-'}
              </Badge>
            </Group>
            {jobProfileId && (
              <Group gap='sm'>
                <Badge size='lg' color='teal' variant='filled' radius='md'>
                  Job Profile: {jobProfileId}
                </Badge>
                <Button variant='light' size='sm' onClick={clearJobProfileFilter} radius='md'>
                  Clear Filter
                </Button>
              </Group>
            )}
            {selectionChanged && (
              <Button variant='light' size='sm' onClick={handleRestoreCurriculum} maw={250}>
                Restore Removed Curriculum
              </Button>
            )}
          </Stack>
        </Paper>

        {!isFilterReady ? (
          <Paper p='xl' radius='md' shadow='sm' withBorder>
            <Text size='sm'>{emptyStateMessage}</Text>
          </Paper>
        ) : (
          <Card>
            <Stack gap='lg'>
              {/* Search Section */}
              <form onSubmit={handleSearchSubmit}>
                <Flex gap='md' align='center'>
                  <TextInput
                    placeholder='Search competencies...'
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    style={{ flex: 1 }}
                    leftSection={<IconSearch size={16} />}
                    rightSection={
                      searchInput && (
                        <ActionIcon
                          variant='subtle'
                          color='gray'
                          onClick={handleClearSearch}
                          size='sm'
                        >
                          ×
                        </ActionIcon>
                      )
                    }
                  />
                  <Button type='submit' leftSection={<IconSearch size={16} />}>
                    Search
                  </Button>
                  {(competencyType === 'Core Competency' ||
                    competencyType === 'Leader Competency' ||
                    competencyType === 'FM Competency' ||
                    (competencyType === 'Technical Competency' && activeTab === 'dacum') ||
                    (competencyType === 'Technical Competency' && activeTab === 'lpd')) && (
                    <Tooltip
                      label={showOnlyUnassigned ? 'Show all competencies' : 'Show only unassigned'}
                      withArrow
                    >
                      <Button
                        variant={showOnlyUnassigned ? 'filled' : 'light'}
                        color={showOnlyUnassigned ? 'blue' : 'gray'}
                        leftSection={
                          showOnlyUnassigned ? (
                            <IconFilter size={18} />
                          ) : (
                            <IconFilterOff size={18} />
                          )
                        }
                        onClick={() => setShowOnlyUnassigned(!showOnlyUnassigned)}
                      >
                        {showOnlyUnassigned ? 'Unassigned Only' : 'Show Unassigned'}
                      </Button>
                    </Tooltip>
                  )}
                </Flex>
              </form>

              {/* Bulk Action Button */}
              {competencyType === 'Technical Competency' &&
                activeTab === 'lpd' &&
                selectedCompetencies.size > 0 && (
                  <Group>
                    <Button
                      onClick={handleBulkAction}
                      leftSection={<IconPlus size={18} />}
                      variant='filled'
                      color='green'
                    >
                      Assign Courses to Selected ({selectedCompetencies.size})
                    </Button>
                    <Button
                      onClick={() => setSelectedCompetencies(new Set())}
                      variant='light'
                      color='gray'
                    >
                      Clear Selection
                    </Button>
                  </Group>
                )}

              {competencyType === 'Technical Competency' ? (
                <Tabs
                  value={activeTab}
                  onChange={(value) => setActiveTab(value || 'lpd')}
                  variant='default'
                >
                  <Tabs.List>
                    <Tabs.Tab value='lpd' leftSection={<IconEye size={16} />}>
                      From LPD
                    </Tabs.Tab>
                    <Tabs.Tab value='dacum' leftSection={<IconPlus size={16} />}>
                      DACUM Competency
                    </Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value='lpd' pt='md'>
                    <DataTables
                      columns={columns}
                      data={displayData}
                      loading={isLoading}
                      withBulkData
                      totalData={curriculumMappingResp?.totalRecords || 0}
                      currentPage={pageNumber}
                      currentSize={pageSize}
                      handleChangePage={handleChangePage}
                      handleChangeSize={handleChangeSize}
                      emptyMessageTitle={
                        selectionChanged
                          ? 'All selected competencies removed'
                          : 'No detail competency data found'
                      }
                      emptyMessageDescription={
                        selectionChanged
                          ? 'Use "Restore removed competency" to bring the records back.'
                          : 'Adjust the filters or choose another job band to continue.'
                      }
                    />
                  </Tabs.Panel>

                  <Tabs.Panel value='dacum' pt='md'>
                    <DataTables
                      columns={dacumColumns}
                      data={displayData}
                      loading={isLoadingDacum}
                      withBulkData
                      totalData={dacumCompetenciesResp?.totalRecords || 0}
                      currentPage={dacumPageNumber}
                      currentSize={dacumPageSize}
                      handleChangePage={handleDacumChangePage}
                      handleChangeSize={handleDacumChangeSize}
                      emptyMessageTitle='No DACUM competencies found'
                      emptyMessageDescription='Add competencies from the LPD tab or create new ones.'
                    />
                  </Tabs.Panel>
                </Tabs>
              ) : (
                <DataTables
                  columns={columns}
                  data={displayData}
                  loading={
                    competencyType === 'Core Competency' ||
                    competencyType === 'Leader Competency' ||
                    competencyType === 'FM Competency'
                      ? isLoadingCore
                      : false
                  }
                  withBulkData
                  totalData={
                    competencyType === 'Core Competency' ||
                    competencyType === 'Leader Competency' ||
                    competencyType === 'FM Competency'
                      ? coreCompetencyResp?.totalRecords || 0
                      : displayData.length
                  }
                  currentPage={pageNumber}
                  currentSize={pageSize}
                  handleChangePage={handleChangePage}
                  handleChangeSize={handleChangeSize}
                  emptyMessageTitle='No detail competency data found'
                  emptyMessageDescription='Adjust the filters or choose another job band to continue.'
                />
              )}
            </Stack>
          </Card>
        )}
      </Stack>

      {/* Bulk  Selection Modal */}
      <BulkCourseSelectionModal
        opened={bulkModalOpened}
        onClose={closeBulkModal}
        onSubmit={handleBulkCourseSubmit}
        selectedCount={selectedCompetencies.size}
        isCreating={bulkCreateMutation.isPending}
        selectedCompetenciesData={curriculumSelection.filter((item) =>
          selectedCompetencies.has(item.competencyId)
        )}
      />
    </Container>
  );
};

// Bulk Course Selection Modal Component
interface BulkCourseSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_data: {
    competencies: any[];
    courses: any[];
    onProgress?: (index: number, total: number) => void;
  }) => void;
  selectedCount: number;
  isCreating: boolean;
  selectedCompetenciesData: CurriculumMappingRecord[];
}

const BulkCourseSelectionModal: React.FC<BulkCourseSelectionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  selectedCount,
  isCreating,
  selectedCompetenciesData
}) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [debouncedSearchKeyword] = useDebouncedValue(searchKeyword, 300);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearchKeyword]);
  const [selectedCourse, setSelectedCourse] = React.useState<any>(null);
  const [rowLpdCourses, setRowLpdCourses] = React.useState<
    Record<number, { courseId: string; courseName: string } | null>
  >({});
  const [processingStatus, setProcessingStatus] = React.useState<
    Record<number, 'pending' | 'processing' | 'success' | 'error'>
  >({});
  const [currentProcessingIndex, setCurrentProcessingIndex] = React.useState<number>(-1);

  const expandedCompetencies = React.useMemo(() => {
    const expanded: Array<{
      competency: CurriculumMappingRecord;
      jobPositionId: string;
      jobPositionName: string;
      originalIndex: number;
    }> = [];

    selectedCompetenciesData.forEach((comp, index) => {
      const jobs = (comp as any).job;
      if (jobs && Array.isArray(jobs) && jobs.length > 0) {
        jobs.forEach((job: any) => {
          if (job.jobPositionId && job.jobPositionName) {
            expanded.push({
              competency: comp,
              jobPositionId: job.jobPositionId,
              jobPositionName: job.jobPositionName,
              originalIndex: index
            });
          }
        });
      } else if (comp.jobPositionId && comp.jobPositionName) {
        expanded.push({
          competency: comp,
          jobPositionId: comp.jobPositionId,
          jobPositionName: comp.jobPositionName,
          originalIndex: index
        });
      } else {
        // Fallback for competencies without job position
        expanded.push({
          competency: comp,
          jobPositionId: '',
          jobPositionName: '-',
          originalIndex: index
        });
      }
    });

    return expanded;
  }, [selectedCompetenciesData]);

  // Clear all states when modal closes
  React.useEffect(() => {
    if (!opened) {
      setSearchKeyword('');
      setPage(1);
      setSelectedCourse(null);
      setRowLpdCourses({});
      setProcessingStatus({});
      setCurrentProcessingIndex(-1);
    } else {
      // Initialize all rows as pending when modal opens
      const initialStatus: Record<number, 'pending' | 'processing' | 'success' | 'error'> = {};
      expandedCompetencies.forEach((_, index) => {
        initialStatus[index] = 'pending';
      });
      setProcessingStatus(initialStatus);
    }
  }, [opened, expandedCompetencies]);

  // Initialize LPD courses per expanded row on load
  React.useEffect(() => {
    if (!opened || expandedCompetencies.length === 0) return;

    const initialLpdCourses: Record<number, { courseId: string; courseName: string } | null> = {};
    expandedCompetencies.forEach((item, index) => {
      if (item.competency.idCourse && item.competency.courseName) {
        initialLpdCourses[index] = {
          courseId: item.competency.idCourse,
          courseName: item.competency.courseName
        };
      } else {
        initialLpdCourses[index] = null;
      }
    });

    setRowLpdCourses(initialLpdCourses);
  }, [opened, expandedCompetencies]);

  // Get set of course IDs to exclude from the course list (both LPD and newly selected)
  const excludedCourseIds = React.useMemo(() => {
    const ids = new Set<string>();
    Object.values(rowLpdCourses).forEach((lpd) => {
      if (lpd?.courseId) ids.add(lpd.courseId);
    });
    if (selectedCourse?.courseId) {
      ids.add(selectedCourse.courseId);
    }
    return ids;
  }, [rowLpdCourses, selectedCourse]);

  const { data, isFetching } = useQuery({
    queryKey: ['bulk-course-selector', debouncedSearchKeyword, page],
    queryFn: () =>
      courseService.list({
        search: debouncedSearchKeyword,
        pageNumber: page,
        pageSize: 10
      }),
    placeholderData: (previousData) => previousData,
    enabled: opened
  });

  // Filter out courses that already exist in competencies
  const courses = React.useMemo(() => {
    const allCourses = (data?.data || []).map((item: any) => ({
      courseId: item.msLCMSCourseID || item.courseId || item.id,
      courseName: item.courseName || item.name || '-',
      courseCode: item.courseCode || item.code,
      courseCategory: item.courseCategory || item.category,
      competencyCategory: item.competencyCategory
    }));

    // Filter out courses that exist in selected competencies
    return allCourses.filter((course: any) => !excludedCourseIds.has(course.courseId));
  }, [data?.data, excludedCourseIds]);

  const totalPages = data?.totalPages || 1;

  const handleSelectCourse = (course: any) => {
    setSelectedCourse(course);
  };

  const handleClearSelectedCourse = () => {
    setSelectedCourse(null);
  };

  const handleClearAllLpdCourses = () => {
    if (
      !window.confirm(
        'Are you sure you want to clear all courses from LPD? This action cannot be undone.'
      )
    ) {
      return;
    }
    const cleared: Record<number, null> = {};
    Object.keys(rowLpdCourses).forEach((key) => {
      cleared[Number(key)] = null;
    });
    setRowLpdCourses(cleared);
  };

  const handleConfirm = () => {
    const competenciesToSubmit = expandedCompetencies.map((item, index) => {
      const lpdCourse = rowLpdCourses[index];
      const newCourse = selectedCourse;
      const finalCourse = newCourse || lpdCourse;

      const competencyRecord: any = {
        ...item.competency,
        jobPositionId: item.jobPositionId,
        jobPositionName: item.jobPositionName,
        idCourse: finalCourse?.courseId || item.competency.idCourse,
        courseName: finalCourse?.courseName || item.competency.courseName
      };

      return competencyRecord;
    });

    const competenciesWithCourses = competenciesToSubmit.filter((comp) => comp.idCourse);

    const seenKeys = new Set<string>();
    const deduplicatedCompetencies = competenciesWithCourses.filter((comp) => {
      const key = [
        (comp.jobPositionId || '').toLowerCase().trim(),
        (comp.competencyName || '').toLowerCase().trim(),
        (comp.category || '').toLowerCase().trim(),
        String(comp.level ?? '')
      ].join('|||');
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });

    const coursesToSubmit = selectedCourse
      ? [selectedCourse]
      : Array.from(
          new Map(
            Object.values(rowLpdCourses)
              .filter((lpd) => lpd !== null)
              .map((lpd) => [
                lpd!.courseId,
                { courseId: lpd!.courseId, courseName: lpd!.courseName }
              ])
          ).values()
        );

    // Progress callback to update status
    const onProgress = (index: number, total: number) => {
      setCurrentProcessingIndex(index);
      setProcessingStatus((prev) => ({
        ...prev,
        [index]: 'processing'
      }));
    };

    onSubmit({
      competencies: deduplicatedCompetencies,
      courses: coursesToSubmit,
      onProgress
    });
  };

  const totalLpdCourses = Object.values(rowLpdCourses).filter((c) => c !== null).length;
  const rowsWithCourse = expandedCompetencies.filter((_, index) => {
    const lpdCourse = rowLpdCourses[index];
    const finalCourse = selectedCourse || lpdCourse;
    return finalCourse !== null;
  }).length;

  return (
    <Modal
      opened={opened}
      onClose={!isCreating ? onClose : () => {}}
      centered
      size='95%'
      title={
        <Stack gap={4}>
          <Text fw={700} size='xl'>
            Bulk Course Assignment
          </Text>
          <Text size='sm' c='dimmed'>
            {expandedCompetencies.length} rows will be processed • {selectedCount} unique
            competencies selected
          </Text>
        </Stack>
      }
      closeOnClickOutside={!isCreating}
      closeOnEscape={!isCreating}
    >
      <Stack gap='lg'>
        {/* Course Selection Section - Moved to Top */}
        <Paper p='lg' withBorder radius='md' bg='gray.0'>
          <Stack gap='md'>
            <Group justify='space-between' align='center'>
              <div>
                <Text fw={700} size='lg' mb={4}>
                  Step 1: Select Course
                </Text>
                <Text size='sm' c='dimmed'>
                  Choose one course to assign to all {expandedCompetencies.length} rows below
                </Text>
              </div>
              {selectedCourse && (
                <Button
                  variant='light'
                  color='red'
                  size='sm'
                  onClick={handleClearSelectedCourse}
                  leftSection={<IconX size={16} />}
                >
                  Clear Selection
                </Button>
              )}
            </Group>

            {selectedCourse ? (
              <Paper p='md' withBorder bg='blue.0' radius='md'>
                <Group justify='space-between'>
                  <div>
                    <Text fw={600} size='md' c='blue.9'>
                      ✓ {selectedCourse.courseName}
                    </Text>
                    <Group gap='xs' mt={4}>
                      <Badge size='sm' variant='light'>
                        {selectedCourse.courseCode}
                      </Badge>
                      <Badge size='sm' variant='light' color='green'>
                        {selectedCourse.courseCategory}
                      </Badge>
                    </Group>
                  </div>
                  <Text size='xs' c='dimmed'>
                    Will be applied to all {expandedCompetencies.length} rows
                  </Text>
                </Group>
              </Paper>
            ) : (
              <Paper p='md' withBorder radius='md' style={{ borderStyle: 'dashed' }}>
                <Text size='sm' c='dimmed' ta='center'>
                  No course selected yet. Search and select ONE course below.
                </Text>
              </Paper>
            )}

            {/* Only show course search and list when no course is selected */}
            {!selectedCourse && (
              <>
                <TextInput
                  placeholder='Search courses by name, code, or category...'
                  value={searchKeyword}
                  onChange={(event) => {
                    setSearchKeyword(event.currentTarget.value);
                  }}
                  leftSection={<IconSearch size={18} />}
                  size='md'
                  radius='md'
                />
                {isFetching ? (
                  <Flex justify='center' align='center' h={200}>
                    <Loader size='md' />
                  </Flex>
                ) : courses.length === 0 ? (
                  <Paper p='xl' withBorder radius='md'>
                    <Stack align='center' gap='xs'>
                      <Text size='sm' c='dimmed' ta='center'>
                        {excludedCourseIds.size > 0
                          ? 'No additional courses available. All courses are already in use.'
                          : 'No courses found. Try adjusting your search.'}
                      </Text>
                    </Stack>
                  </Paper>
                ) : (
                  <>
                    <Paper p='xs' withBorder radius='md' bg='yellow.0'>
                      <Text size='xs' c='yellow.9' ta='center' fw={500}>
                        ⚠️ Select ONE course only - it will be assigned to all{' '}
                        {expandedCompetencies.length} competencies
                      </Text>
                    </Paper>
                    <ScrollArea h={250} offsetScrollbars>
                      <Table highlightOnHover withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 60 }}>Select</Table.Th>
                            <Table.Th>Course Name</Table.Th>
                            <Table.Th>Code</Table.Th>
                            <Table.Th>Category</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {courses.map((course: any) => {
                            const isSelected = selectedCourse?.courseId === course.courseId;
                            return (
                              <Table.Tr
                                key={course.courseId}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: isSelected
                                    ? 'var(--mantine-color-blue-0)'
                                    : undefined
                                }}
                                onClick={() => handleSelectCourse(course)}
                              >
                                <Table.Td>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleSelectCourse(course)}
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <Text fw={isSelected ? 600 : 400} size='sm'>
                                    {course.courseName}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge size='sm' variant='light'>
                                    {course.courseCode || '-'}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Text size='sm'>{course.courseCategory || '-'}</Text>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                    <Group justify='center'>
                      <Pagination
                        total={Math.max(totalPages, 1)}
                        value={page}
                        onChange={setPage}
                        size='sm'
                      />
                    </Group>
                  </>
                )}
              </>
            )}

            {/* Show message when course is selected */}
            {selectedCourse && (
              <Paper p='md' withBorder radius='md' bg='green.0'>
                <Group justify='center' gap='xs'>
                  <Text size='sm' c='green.9' fw={500} ta='center'>
                    ✓ Course selected! Proceed to Step 2 to review or click "Clear Selection" to
                    choose a different course.
                  </Text>
                </Group>
              </Paper>
            )}
          </Stack>
        </Paper>

        {/* Selected Competencies Table with Status */}
        <Paper p='lg' withBorder radius='md'>
          <Stack gap='md'>
            <Group justify='space-between' align='center'>
              <div>
                <Text fw={700} size='lg' mb={4}>
                  Step 2: Review Competencies ({expandedCompetencies.length} rows)
                </Text>
                <Text size='sm' c='dimmed'>
                  All rows below will be processed individually with the selected course
                </Text>
              </div>
              <Group gap='xs'>
                <Badge size='lg' color='gray' variant='light'>
                  Total: {expandedCompetencies.length}
                </Badge>
                <Badge size='lg' color='blue' variant='light'>
                  Ready: {rowsWithCourse}
                </Badge>
                {totalLpdCourses > 0 && (
                  <Button variant='subtle' color='red' size='xs' onClick={handleClearAllLpdCourses}>
                    Clear LPD Courses
                  </Button>
                )}
              </Group>
            </Group>

            <ScrollArea h={350} offsetScrollbars>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 50 }}>#</Table.Th>
                    <Table.Th style={{ width: 100 }}>Status</Table.Th>
                    <Table.Th>Job Position</Table.Th>
                    <Table.Th>Competency</Table.Th>
                    <Table.Th style={{ width: 80 }}>Level</Table.Th>
                    <Table.Th>Course Assignment</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {expandedCompetencies.map((item, index) => {
                    const { competency: comp, jobPositionName } = item;
                    const lpdCourse = rowLpdCourses[index];
                    const finalCourse = selectedCourse || lpdCourse;
                    const status = processingStatus[index] || 'pending';

                    const statusConfig = {
                      pending: { color: 'gray', label: 'Pending', icon: '○' },
                      processing: { color: 'blue', label: 'Processing', icon: '◐' },
                      success: { color: 'green', label: 'Success', icon: '✓' },
                      error: { color: 'red', label: 'Failed', icon: '✗' }
                    }[status];

                    return (
                      <Table.Tr
                        key={`${comp.competencyId}-${item.jobPositionId}-${index}`}
                        style={{
                          backgroundColor:
                            status === 'processing'
                              ? 'var(--mantine-color-blue-0)'
                              : status === 'success'
                                ? 'var(--mantine-color-green-0)'
                                : status === 'error'
                                  ? 'var(--mantine-color-red-0)'
                                  : undefined
                        }}
                      >
                        <Table.Td>
                          <Text size='sm' fw={500}>
                            {index + 1}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size='md'
                            color={statusConfig.color}
                            variant={status === 'pending' ? 'light' : 'filled'}
                          >
                            {statusConfig.icon} {statusConfig.label}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size='sm' fw={500}>
                            {jobPositionName}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <div>
                            <Text size='sm' fw={500}>
                              {comp.competencyName}
                            </Text>
                            <Text size='xs' c='dimmed'>
                              {comp.category}
                            </Text>
                          </div>
                        </Table.Td>
                        <Table.Td>
                          <Badge size='md'>{comp.level}</Badge>
                        </Table.Td>
                        <Table.Td>
                          {finalCourse ? (
                            <div>
                              <Text size='sm' fw={500} c={selectedCourse ? 'blue' : 'dimmed'}>
                                {finalCourse.courseName}
                              </Text>
                              <Text size='xs' c='dimmed'>
                                {selectedCourse ? 'New selection' : 'From LPD'}
                              </Text>
                            </div>
                          ) : (
                            <Text size='sm' c='red'>
                              No course assigned
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        </Paper>
      </Stack>

      <Group
        justify='space-between'
        mt='lg'
        pt='lg'
        style={{ borderTop: '2px solid var(--mantine-color-gray-2)' }}
      >
        <Stack gap={4}>
          <Text size='sm' fw={600}>
            Ready to submit: {rowsWithCourse} / {expandedCompetencies.length} rows
          </Text>
          {isCreating && currentProcessingIndex >= 0 && (
            <Text size='xs' c='dimmed'>
              Processing row {currentProcessingIndex + 1} of {expandedCompetencies.length}...
            </Text>
          )}
        </Stack>
        <Group>
          <Button variant='default' onClick={onClose} disabled={isCreating} size='md'>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={rowsWithCourse === 0 || isCreating}
            loading={isCreating}
            size='md'
            color='blue'
          >
            {isCreating
              ? `Processing ${currentProcessingIndex + 1}/${expandedCompetencies.length}`
              : `Submit ${rowsWithCourse} Row${rowsWithCourse !== 1 ? 's' : ''}`}
          </Button>
        </Group>
      </Group>
    </Modal>
  );
};

export default DetailCompetencyPage;
